import React, { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import io from "socket.io-client";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, Tooltip } from "@mui/material";
import SoundLevelGaugeContainer from "./SoundLevelGaugeContainer";
import { keyframes } from '@emotion/react';
import { useAuthContext } from "../hooks/useAuthContext";
import { Link } from "react-router-dom"; // 引入 Link 組件
import SettingsIcon from '@mui/icons-material/Settings'; // 引入設置圖標

// 在組件外部定義動畫
const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
  }
  70% {
    box-shadow: 0 0 0 5px rgba(76, 175, 80, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
`;

const DeviceStatusTable = () => {
  const [devices, setDevices] = useState([]); // State to store device data
  const [dialogOpen, setDialogOpen] = useState(false); // 控制對話框的開關
  const [selectedClientId, setSelectedClientId] = useState(null); // 儲存被選中的 clientId
  const { user } = useAuthContext();

  useEffect(() => {
    // 使用相對 URL，通過 nginx proxy
    const socket = io(`${window.location.protocol}//${window.location.host}`, {
      transports: ['websocket'],
      path: '/socket.io',
      auth: { token: user?.token },
    });

    socket.on("device_status_change", (data) => {
      setDevices((prevDevices) => {
        const existingDeviceIndex = prevDevices.findIndex(
          (device) => device.clientId === data.clientId
        );

        if (existingDeviceIndex !== -1) {
          const updatedDevices = [...prevDevices];
          updatedDevices[existingDeviceIndex] = {
            ...updatedDevices[existingDeviceIndex],
            status: data.status,
            updatedAt: data.timestamp,
            ...(data.version && { version: data.version }),
          };
          return updatedDevices;
        } else {
          return [...prevDevices, data];
        }
      });
    });

    const fetchDeviceList = async () => {
      try {
        const response = await fetch('/api/v1/device/list', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${user.token}`,
          }
        });
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const data = await response.json();
        console.log("Initial device list:", data);
        setDevices(data);
      } catch (error) {
        console.error("Error fetching device list:", error);
      }
    };
    fetchDeviceList();

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleButtonClick = (clientId) => {
    setSelectedClientId(clientId); // 設定被選中的 clientId
    setDialogOpen(true); // 打開對話框
  };

  const handleDialogClose = () => {
    setDialogOpen(false); // 關閉對話框
    setSelectedClientId(null); // 清除選中的 clientId
  };

  const columns = [
    { field: "clientId", headerName: "Client ID", width: 150 },
    { field: "clientName", headerName: "Client Name", width: 150 },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      renderCell: (params) => (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center',
          paddingLeft: '16px',
          width: '100%',
          height: '100%',
        }}>
          <span
            style={{
              display: 'inline-block',
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              backgroundColor: params.value === "online" ? "#4caf50" : "#f44336",
              boxShadow: `0 0 4px ${params.value === "online" ? "#4caf50" : "#f44336"}`,
              animation: params.value === "online" ? `${pulse} 1.5s infinite` : "none",
            }}
          />
        </div>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      renderCell: (params) => (
        <div style={{ 
          display: 'flex', 
          gap: '8px', 
          alignItems: 'center', 
          justifyContent: 'flex-start', // 左對齊
          height: '100%', // 確保 div 佔滿單元格高度
          paddingLeft: '8px' // 提供一些左側填充
        }}>
          <Button
            variant="contained"
            size="small"
            onClick={() => handleButtonClick(params.row.clientId)}
            disabled={params.row.status !== "online"}
            style={{ minWidth: '60px' }} // 確保按鈕寬度一致
          >
            SPL
          </Button>
          <Button
            variant="outlined"
            size="small"
            component={Link}
            to={`/operator/connected-devices/${params.row.clientId}/setting`}
            startIcon={<SettingsIcon />}
            disabled={params.row.status !== "online"}
          >
            設定
          </Button>
        </div>
      ),
    },
    {
      field: "version",
      headerName: "Version",
      // Fits SemVer builds such as 2026-10-08_v0.4.3-dev.32+gd0b8d7e0.dirty.devsrv.dmic (~50 chars);
      // anything longer is cut with an ellipsis and shown in full on hover
      width: 400,
      renderCell: (params) => (
        <Tooltip title={params.value || ""} placement="top-start">
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {params.value}
          </span>
        </Tooltip>
      ),
    },
    {
      field: "updatedAt",
      headerName: "Updated At",
      width: 180,
    },
    {
      field: "createdAt",
      headerName: "Created At",
      width: 180,
    },
  ];

  return (
    <>
      <div style={{ height: "100%", width: "100%", overflowX: "auto" }}>
        <h2>Device Status</h2>
        <DataGrid
          rows={devices.map((device, index) => ({
            id: index, // Use index as unique ID for DataGrid rows
            ...device,
          }))}
          columns={columns}
          pageSize={25}
          rowsPerPageOptions={[5, 10, 25]}
          disableSelectionOnClick
        />
      </div>

      {/* Dialog 元件 */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleDialogClose} 
        fullScreen // 設置對話框為全螢幕
      >
        <DialogTitle>Temporary for SPL display ... </DialogTitle>
        <DialogContent>
          <p>Client ID: {selectedClientId}</p>
          <Grid container spacing={4}>
            <Grid size={2}/>
            <Grid size={8}>
              <SoundLevelGaugeContainer clientId={selectedClientId} />
            </Grid>
            <Grid size={2}/>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DeviceStatusTable;
